import {
  default as superAgentRequest,
  ProgressEvent,
  SuperAgentStatic,
  SuperAgentRequest,
  Response,
} from 'superagent';

import {getGlobalRequestHandlers} from './utilities/globalRequestHandlers';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type ConfigureAgentHandler = (
  agent: SuperAgentStatic
) => SuperAgentStatic;

export type ConfigureRequestHandler = (
  agent: SuperAgentRequest
) => SuperAgentRequest;

export type CallbackHandler = (err: any, res?: Response) => void;

export type AccountingExportPdfCreatedNotification = {
  accountingExportId: string;
  handle: string;
  pushNotificationType: 'AccountingExportPdfCreatedNotification';
};

export type ActivityEntity = {
  day: string;
  id: string;
  type: 'ActivityEntity';
};

export type ActivitySharedNotification = {
  context: 'TIMETRACKING';
  entity: ActivityEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ActivitySharedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type CasusEntity = {
  id: string;
  type: 'CasusEntity';
};

export type CasusSpecificPushNotifications = {
  pushNotificationType:
    | 'DocumentPdfCreatedPushNotification'
    | 'DocumentPdfCreationFailedPushNotification'
    | 'DocumentPdfSignedPushNotification'
    | 'DocumentPdfSigningFailedPushNotification'
    | 'DocumentPreviewPdfCreatedPushNotification'
    | 'ZipExtractedPushNotification'
    | 'ZipExtractionFailedPushNotification';
};

export type CommunicationPushNotification = {
  needsAttention: boolean;
  pushNotificationType: 'CommunicationPushNotification';
};

export type CommunicationSummaryResponse = {
  needsAttention: boolean;
};

export type CommunicationUserSpecificPushNotifications = {
  pushNotificationType:
    | 'CommunicationPushNotification'
    | 'EmailAccountAuthenticationFailedPushNotification'
    | 'NewEmailReceivedPushNotification';
};

export type DocumentEntity = {
  casusId: string;
  documentVersionId: string;
  folderId?: string;
  id: string;
  type: 'DocumentEntity';
};

export type DocumentManagementUserSpecificPushNotifications = {
  pushNotificationType:
    | 'TemporaryDocumentCreatedPushNotification'
    | 'TemporaryDocumentPdfCreatedPushNotification'
    | 'TemporaryDocumentPdfCreationFailedPushNotification'
    | 'UserAddedNewDocumentVersionPushNotification';
};

export type DocumentPdfCreatedPushNotification = {
  casusId: string;
  documentId: string;
  pushNotificationType: 'DocumentPdfCreatedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfCreationFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'DocumentPdfCreationFailedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfSignedPushNotification = {
  casusId: string;
  documentId: string;
  pushNotificationType: 'DocumentPdfSignedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfSigningFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'DocumentPdfSigningFailedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPreviewPdfCreatedPushNotification = {
  casusId: string;
  documentId: string;
  documentVersionId: string;
  pushNotificationType: 'DocumentPreviewPdfCreatedPushNotification';
};

export type DocumentSharedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: DocumentEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'DocumentSharedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type DocumentUploadFinishedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'DocumentUploadFinishedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type DownloadExpiredCondition = {
  conditionType: 'DownloadExpiredCondition';
};

export type EmailAccountAuthenticationFailedPushNotification = {
  emailAccountId: string;
  name: string;
  pushNotificationType: 'EmailAccountAuthenticationFailedPushNotification';
  userName: string;
};

export type Entity = {
  id: string;
  type: string;
};

export type FeeDeletedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'FeeDeletedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type FeesShouldBeCompletedNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'FeesShouldBeCompletedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type FinanceExportUserSpecificPushNotifications = {
  pushNotificationType: 'AccountingExportPdfCreatedNotification';
};

export type InaccessibleActivityCreatedNotification = {
  context: 'TIMETRACKING';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InaccessibleActivityCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InformativeObligationVersionDeadlineReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InformativeObligationVersionDeadlineReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerCompleteByDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: InvoiceContainerEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceContainerCompleteByDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: InvoiceContainerEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceContainerDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerEntity = {
  id: string;
  type: 'InvoiceContainerEntity';
};

export type InvoiceDocumentsCreatedNotification = {
  invoiceContainerId: string;
  pushNotificationType: 'InvoiceDocumentsCreatedNotification';
};

export type InvoiceManagementUserSpecificPushNotifications = {
  pushNotificationType:
    | 'InvoiceDocumentsCreatedNotification'
    | 'InvoicePreviewPdfCreatedNotification'
    | 'ReceiptPdfExportReadyNotification'
    | 'TransactionSetPdfExportReadyNotification';
};

export type InvoicePreviewPdfCreatedNotification = {
  invoiceContainerId: string;
  pushNotificationType: 'InvoicePreviewPdfCreatedNotification';
};

export type InvoiceReminderDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceReminderDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type JustitiaSubmissionReceivedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: SubmissionEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'JustitiaSubmissionReceivedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type NewEmailReceivedPushNotification = {
  emailId: string;
  pushNotificationType: 'NewEmailReceivedPushNotification';
};

export type Notification = {
  context:
    | 'CASUS_MANAGEMENT'
    | 'DOCUMENT_MANAGEMENT'
    | 'TASK_MANAGEMENT'
    | 'INVOICE_MANAGEMENT'
    | 'OBLIGATION_MANAGEMENT'
    | 'TIMETRACKING';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name:
    | 'TaskAssignedNotification'
    | 'ActivitySharedNotification'
    | 'SetAsResponsiblePersonOfTheCasusNotification'
    | 'RemovedAsResponsiblePersonOfTheCasusNotification'
    | 'DocumentSharedNotification'
    | 'ZipFileCreatedNotification'
    | 'ZipFileCreationFailedNotification'
    | 'TaskDueDateReachedNotification'
    | 'DocumentUploadFinishedNotification'
    | 'FeesShouldBeCompletedNotification'
    | 'InvoiceContainerCompleteByDueDateReachedNotification'
    | 'InvoiceContainerDueDateReachedNotification'
    | 'InvoiceReminderDueDateReachedNotification'
    | 'ObligationVersionReviewNeededNotification'
    | 'ObligationVersionCreatedNotification'
    | 'ObligationVersionApprovedNotification'
    | 'ObligationVersionRejectedNotification'
    | 'ObligationReminderTimestampReachedNotification'
    | 'InformativeObligationVersionDeadlineReachedNotification'
    | 'ResolvableObligationVersionDeadlineReachedNotification'
    | 'InaccessibleActivityCreatedNotification'
    | 'FeeDeletedNotification'
    | 'JustitiaSubmissionReceivedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE' | 'RESOLVABLE';
};

export type NotificationCenterPushNotification = {
  addedNotifications: boolean;
  notificationSummary: NotificationSummary;
  pushNotificationType: 'NotificationCenterPushNotification';
};

export type NotificationSettings = {
  grouped: boolean;
  showOnlyImportant: boolean;
  visibleNotifications: 'ALL' | 'INFORMATIVE' | 'RESOLVABLE';
};

export type NotificationSummary = {
  hasImportantNotification: boolean;
  unreadInformativeNotifications: number;
  unreadResolvableNotifications: number;
};

export type NotificationSummaryResponse = {
  notificationSummary: NotificationSummary;
};

export type NotificationUpdateParams = {
  notifyAt: string;
};

export type NotificationUserSpecificPushNotifications = {
  pushNotificationType: 'NotificationCenterPushNotification';
};

export type NotificationsResponse = {
  notifications: Array<Notification>;
  settings: NotificationSettings;
  summary: NotificationSummary;
};

export type ObligationEntity = {
  id: string;
  obligationVersionId: string;
  type: 'ObligationEntity';
};

export type ObligationReminderEntity = {
  id: string;
  obligationId: string;
  type: 'ObligationReminderEntity';
};

export type ObligationReminderTimestampReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationReminderEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationReminderTimestampReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationResponse = {
  deadline: string;
  id: string;
  note: string;
  responsibleUser: UserInResponse;
};

export type ObligationVersionApprovedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionApprovedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionCreatedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionRejectedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionRejectedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionReviewNeededNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionReviewNeededNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ReceiptPdfExportReadyNotification = {
  downloadUrl: string;
  handle: string;
  id: string;
  pushNotificationType: 'ReceiptPdfExportReadyNotification';
};

export type RemovedAsResponsiblePersonOfTheCasusNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'RemovedAsResponsiblePersonOfTheCasusNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ResolvableObligationVersionDeadlineReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ResolvableObligationVersionDeadlineReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'RESOLVABLE';
};

export type ResolvableObligationVersionDeadlineReachedResolutionParams = {
  obligationState: 'OBSOLETE' | 'FULFILLED' | 'MISSED';
  resolution: 'CHANGE_STATE';
  type: 'ResolvableObligationVersionDeadlineReachedNotification';
};

export type SetAsResponsiblePersonOfTheCasusNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'SetAsResponsiblePersonOfTheCasusNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type SubmissionEntity = {
  casusId: string;
  id: string;
  type: 'SubmissionEntity';
};

export type TaskAssignedNotification = {
  context: 'TASK_MANAGEMENT';
  entity: TaskEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'TaskAssignedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type TaskDueDateReachedNotification = {
  context: 'TASK_MANAGEMENT';
  entity: TaskEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'TaskDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'RESOLVABLE';
};

export type TaskDueDateReachedResolutionParams = {
  rescheduleDate?: string;
  resolution: 'RESCHEDULE' | 'COMPLETE';
  type: 'TaskDueDateReachedNotification';
};

export type TaskEntity = {
  casusId: string;
  id: string;
  type: 'TaskEntity';
};

export type TaskResponse = {
  assignedUser?: UserInResponse;
  criticality: 'LOW' | 'MEDIUM' | 'HIGH';
  description: string;
  dueDate?: string;
  id: string;
};

export type TemporaryDocumentCreatedPushNotification = {
  pushNotificationType: 'TemporaryDocumentCreatedPushNotification';
  temporaryDocumentId: string;
  type:
    | 'EMAIL_DOCUMENT'
    | 'EXCEL_DOCUMENT'
    | 'POWERPOINT_DOCUMENT'
    | 'PDF_DOCUMENT'
    | 'WORD_DOCUMENT'
    | 'IMAGE_DOCUMENT'
    | 'TEXT_DOCUMENT'
    | 'RTF_DOCUMENT'
    | 'ZIP_DOCUMENT'
    | 'UNKNOWN_DOCUMENT';
};

export type TemporaryDocumentPdfCreatedPushNotification = {
  pushNotificationType: 'TemporaryDocumentPdfCreatedPushNotification';
  temporaryDocumentId: string;
};

export type TemporaryDocumentPdfCreationFailedPushNotification = {
  pushNotificationType: 'TemporaryDocumentPdfCreationFailedPushNotification';
  temporaryDocumentId: string;
};

export type TransactionSetPdfExportReadyNotification = {
  handle: string;
  pushNotificationType: 'TransactionSetPdfExportReadyNotification';
  transactionSetId: string;
};

export type Unit = {};

export type UserAddedNewDocumentVersionPushNotification = {
  casusId: string;
  documentId: string;
  folderId: string;
  lastModifiedAt: string;
  pushNotificationType: 'UserAddedNewDocumentVersionPushNotification';
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type ZipExtractedPushNotification = {
  casusId: string;
  pushNotificationType: 'ZipExtractedPushNotification';
  zipFileId: string;
};

export type ZipExtractionFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'ZipExtractionFailedPushNotification';
  zipFileId: string;
};

export type ZipFileCreatedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: ZipFileEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ZipFileCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ZipFileCreationFailedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ZipFileCreationFailedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ZipFileEntity = {
  id: string;
  type: 'ZipFileEntity';
};

export type Response_downloadZipFile_200 = {};

export type Response_getEmailIndication_200 = boolean;

export interface ResponseWithBody<S extends number, T> extends Response {
  status: S;
  body: T;
}

export type QueryParameters = {[param: string]: any};

export interface CommonRequestOptions {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
}

type SuperAgentError = {status: number};
const isSuperAgentError = (error: unknown): error is SuperAgentError =>
  Boolean((error as SuperAgentError).status);

/*
 *
 * @class Backend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class Backend {
  private domain: string = '';
  private onUnauthorizedHandler?: CallbackHandler;
  private errorHandlers: CallbackHandler[] = [];
  private requestHeadersHandler?: RequestHeadersHandler;
  private configureAgentHandler?: ConfigureAgentHandler;
  private configureRequestHandler?: ConfigureRequestHandler;

  public constructor(domain?: string, onUnauthorizedHandler?: CallbackHandler) {
    if (domain) {
      this.domain = domain;
    }

    if (onUnauthorizedHandler) {
      this.onUnauthorizedHandler = onUnauthorizedHandler;
    }

    this.getCommunicationSummaryURL =
      this.getCommunicationSummaryURL.bind(this);
    this.getCommunicationSummary = this.getCommunicationSummary.bind(this);
    this.getObligationURL = this.getObligationURL.bind(this);
    this.getObligation = this.getObligation.bind(this);
    this.getTaskURL = this.getTaskURL.bind(this);
    this.getTask = this.getTask.bind(this);
    this.downloadZipFileURL = this.downloadZipFileURL.bind(this);
    this.downloadZipFile = this.downloadZipFile.bind(this);
    this.resetEmailIndicationURL = this.resetEmailIndicationURL.bind(this);
    this.resetEmailIndication = this.resetEmailIndication.bind(this);
    this.getEmailIndicationURL = this.getEmailIndicationURL.bind(this);
    this.getEmailIndication = this.getEmailIndication.bind(this);
    this.deleteAllInformativeNotificationsURL =
      this.deleteAllInformativeNotificationsURL.bind(this);
    this.deleteAllInformativeNotifications =
      this.deleteAllInformativeNotifications.bind(this);
    this.getNotificationsURL = this.getNotificationsURL.bind(this);
    this.getNotifications = this.getNotifications.bind(this);
    this.deleteNotificationURL = this.deleteNotificationURL.bind(this);
    this.deleteNotification = this.deleteNotification.bind(this);
    this.delayURL = this.delayURL.bind(this);
    this.delay = this.delay.bind(this);
    this.markImportantURL = this.markImportantURL.bind(this);
    this.markImportant = this.markImportant.bind(this);
    this.markReadURL = this.markReadURL.bind(this);
    this.markRead = this.markRead.bind(this);
    this.markUnimportantURL = this.markUnimportantURL.bind(this);
    this.markUnimportant = this.markUnimportant.bind(this);
    this.markUnreadURL = this.markUnreadURL.bind(this);
    this.markUnread = this.markUnread.bind(this);
    this.resolveObligationVersionDeadlineReachedNotificationURL =
      this.resolveObligationVersionDeadlineReachedNotificationURL.bind(this);
    this.resolveObligationVersionDeadlineReachedNotification =
      this.resolveObligationVersionDeadlineReachedNotification.bind(this);
    this.resolveTaskDueDateReachedNotificationURL =
      this.resolveTaskDueDateReachedNotificationURL.bind(this);
    this.resolveTaskDueDateReachedNotification =
      this.resolveTaskDueDateReachedNotification.bind(this);
    this.updateSettingsURL = this.updateSettingsURL.bind(this);
    this.updateSettings = this.updateSettings.bind(this);
    this.getNotificationSummaryURL = this.getNotificationSummaryURL.bind(this);
    this.getNotificationSummary = this.getNotificationSummary.bind(this);
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  public setRequestHeadersHandler(handler: RequestHeadersHandler): void {
    this.requestHeadersHandler = handler;
  }

  public setConfigureAgentHandler(handler: ConfigureAgentHandler): void {
    this.configureAgentHandler = handler;
  }

  public setConfigureRequestHandler(handler: ConfigureRequestHandler): void {
    this.configureRequestHandler = handler;
  }

  public request(
    method: string,
    url: string,
    body: any,
    headers: RequestHeaders,
    form: any,
    reject: CallbackHandler,
    resolve: CallbackHandler,
    progress: CallbackHandler,
    opts: CommonRequestOptions,
    signal?: AbortSignal,
    skipRedirectOnUnauthorized = false
  ): void {
    const agent = this.configureAgentHandler
      ? this.configureAgentHandler(superAgentRequest)
      : superAgentRequest;

    if (process.env.NODE_ENV === 'test') {
      throw new Error(
        'Calling endpoints in test environment should be avoided, this endpoint needs to be mocked: ' +
          url +
          ' [' +
          method +
          ']'
      );
    }

    let request = agent(method, url);
    if (this.configureRequestHandler) {
      request = this.configureRequestHandler(request);
    }

    let processedHeaders = headers;
    if (this.requestHeadersHandler) {
      processedHeaders = this.requestHeadersHandler({...headers});
    }

    if (body) {
      request.send(body);

      if (typeof body === 'object' && !(body.constructor.name === 'Buffer')) {
        processedHeaders['Content-Type'] = 'application/json';
      }
    }

    if (Object.keys(form).length > 0) {
      // do not explicitly set the the content type
      // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post#39281156
      delete processedHeaders['content-type'];
      const formData = new FormData();

      Object.keys(form).forEach((key: string) =>
        formData.append(key, form[key])
      );
      request.send(formData);
    }

    request.set(processedHeaders);

    if (opts.$retries && opts.$retries > 0) {
      request.retry(opts.$retries);
    }

    if (
      (opts.$timeout && opts.$timeout > 0) ||
      (opts.$deadline && opts.$deadline > 0)
    ) {
      request.timeout({deadline: opts.$deadline, response: opts.$timeout});
    }

    request
      .on('progress', progress)
      .on('abort', () => reject('Aborted'))
      .ok((res) => res.status <= 400)
      .end((error, response) => {
        // an error will also be emitted for a 4xx and 5xx status code
        // the error object will then have error.status and error.response fields
        // see superagent error handling: https://github.com/visionmedia/superagent/blob/master/docs/index.md#error-handling
        if (error) {
          reject(error);
          if (!skipRedirectOnUnauthorized) {
            this.onUnauthorizedHandler?.(error);
          }
          this.errorHandlers.forEach((handler) => handler(error));
        } else {
          resolve(response);
        }
      });

    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  /*
   *
   * @method
   * @name Backend#getCommunicationSummary
   */
  public async getCommunicationSummary({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CommunicationSummaryResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CommunicationSummaryResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CommunicationSummaryResponse>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getCommunicationSummaryURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CommunicationSummaryResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CommunicationSummaryResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CommunicationSummaryResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CommunicationSummaryResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getCommunicationSummaryURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/communication-summary/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getObligation
   * @param {string} obligationId -
   */
  public async getObligation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      obligationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ObligationResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, ObligationResponse>
          | ResponseWithBody<404, Unit>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, ObligationResponse> | ResponseWithBody<404, Unit>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['obligationId'] === 'undefined') {
        reject(new Error('Missing required  parameter: obligationId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getObligationURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, ObligationResponse>
      | ResponseWithBody<404, Unit>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, ObligationResponse>
          | ResponseWithBody<404, Unit>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ObligationResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, ObligationResponse>
        | ResponseWithBody<404, Unit>,
      header: result.header,
      hasError: true,
    };
  }

  public getObligationURL(
    parameters: {
      obligationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/entity/obligation/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['obligationId'] !== 'undefined') {
      queryParameters['obligationId'] =
        Backend.convertParameterCollectionFormat(
          parameters['obligationId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getTask
   * @param {string} casusId -
   * @param {string} taskId -
   */
  public async getTask({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      taskId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TaskResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (ResponseWithBody<200, TaskResponse> | ResponseWithBody<404, Unit>)
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TaskResponse> | ResponseWithBody<404, Unit>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['taskId'] === 'undefined') {
        reject(new Error('Missing required  parameter: taskId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getTaskURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, TaskResponse>
      | ResponseWithBody<404, Unit>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, TaskResponse>
          | ResponseWithBody<404, Unit>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TaskResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, TaskResponse>
        | ResponseWithBody<404, Unit>,
      header: result.header,
      hasError: true,
    };
  }

  public getTaskURL(
    parameters: {
      casusId: string;
      taskId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/entity/task/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['taskId'] !== 'undefined') {
      queryParameters['taskId'] = Backend.convertParameterCollectionFormat(
        parameters['taskId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadZipFile
   * @param {string} zipFileId -
   */
  public async downloadZipFile({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      zipFileId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadZipFile_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, Response_downloadZipFile_200>
          | ResponseWithBody<404, DownloadExpiredCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, Response_downloadZipFile_200>
      | ResponseWithBody<404, DownloadExpiredCondition>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/zip';

      if (typeof parameters['zipFileId'] === 'undefined') {
        reject(new Error('Missing required  parameter: zipFileId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadZipFileURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, Response_downloadZipFile_200>
      | ResponseWithBody<404, DownloadExpiredCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, Response_downloadZipFile_200>
          | ResponseWithBody<404, DownloadExpiredCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadZipFile_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, Response_downloadZipFile_200>
        | ResponseWithBody<404, DownloadExpiredCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public downloadZipFileURL(
    parameters: {
      zipFileId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/entity/zip-file/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['zipFileId'] !== 'undefined') {
      queryParameters['zipFileId'] = Backend.convertParameterCollectionFormat(
        parameters['zipFileId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#resetEmailIndication
   */
  public async resetEmailIndication({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.resetEmailIndicationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public resetEmailIndicationURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/indications/email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getEmailIndication
   */
  public async getEmailIndication({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getEmailIndication_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getEmailIndication_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getEmailIndication_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getEmailIndicationURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getEmailIndication_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getEmailIndication_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getEmailIndication_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getEmailIndication_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getEmailIndicationURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/indications/email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteAllInformativeNotifications
   */
  public async deleteAllInformativeNotifications({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteAllInformativeNotificationsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteAllInformativeNotificationsURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getNotifications
   */
  public async getNotifications({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getNotificationsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getNotificationsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteNotification
   * @param {string} notificationId -
   */
  public async deleteNotification({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteNotificationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteNotificationURL(
    parameters: {
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#delay
   * @param {} body -
   * @param {string} notificationId -
   */
  public async delay({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: NotificationUpdateParams;
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.delayURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public delayURL(
    parameters: {
      body: NotificationUpdateParams;
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/delay/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markImportant
   * @param {string} notificationId -
   */
  public async markImportant({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.markImportantURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public markImportantURL(
    parameters: {
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/mark-important/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markRead
   * @param {string} notificationId -
   */
  public async markRead({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.markReadURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public markReadURL(
    parameters: {
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/mark-read/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markUnimportant
   * @param {string} notificationId -
   */
  public async markUnimportant({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.markUnimportantURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public markUnimportantURL(
    parameters: {
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/mark-unimportant/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markUnread
   * @param {string} notificationId -
   */
  public async markUnread({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.markUnreadURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public markUnreadURL(
    parameters: {
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/notifications/{notificationId}/mark-unread/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#resolveObligationVersionDeadlineReachedNotification
   * @param {} body -
   * @param {string} notificationId -
   */
  public async resolveObligationVersionDeadlineReachedNotification({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ResolvableObligationVersionDeadlineReachedResolutionParams;
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.resolveObligationVersionDeadlineReachedNotificationURL(
            parameters
          ),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public resolveObligationVersionDeadlineReachedNotificationURL(
    parameters: {
      body: ResolvableObligationVersionDeadlineReachedResolutionParams;
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/notification/notifications/{notificationId}/resolve/obligation-version-deadline-reached-notification/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#resolveTaskDueDateReachedNotification
   * @param {} body -
   * @param {string} notificationId -
   */
  public async resolveTaskDueDateReachedNotification({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: TaskDueDateReachedResolutionParams;
      notificationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['notificationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: notificationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.resolveTaskDueDateReachedNotificationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public resolveTaskDueDateReachedNotificationURL(
    parameters: {
      body: TaskDueDateReachedResolutionParams;
      notificationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/notification/notifications/{notificationId}/resolve/task-due-date-reached-notification/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{notificationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['notificationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateSettings
   * @param {} body -
   */
  public async updateSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: NotificationSettings;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateSettingsURL(
    parameters: {
      body: NotificationSettings;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getNotificationSummary
   */
  public async getNotificationSummary({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: NotificationSummaryResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, NotificationSummaryResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, NotificationSummaryResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getNotificationSummaryURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, NotificationSummaryResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          NotificationSummaryResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as NotificationSummaryResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, NotificationSummaryResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getNotificationSummaryURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/notification/summary/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }
}

const backend = new Backend(
  process.env.REACT_APP_SERVER_URL || '/api',
  (error) => {
    if (error.status === 401) {
      window.location.href =
        '/login/?redirect=' +
        encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
    }
  }
);

backend.setConfigureRequestHandler(
  (request: SuperAgentRequest): SuperAgentRequest => {
    if (new RegExp('/download', 'u').test(request.url)) {
      request.responseType('blob');
    }

    return request;
  }
);

export default backend;
